import { Model } from './model';
import { Observable } from 'rxjs/Observable';
import { Collection } from '../support/collection/collection';
import { ModelInterface } from './model.interface';

export class ApiCollection extends Collection implements Iterable<any> {

    protected service;

    /**
     * Factory method to return new instance of collection
     *
     * @param {Array<any>} data
     * @param {ApiService} service
     * @param {Model} model
     * @returns {ApiCollection}
     */
    public static make(data: Array<any>, service, model?: ModelInterface) {

        return new ApiCollection(data, model, service);

    }

    /**
     * Construct instance of collection
     *
     * @param {Array<any>} data
     * @param {Model} model
     * @param {ApiService} service
     */
    constructor(data?: Array<any>, model?: ModelInterface, service?) {
        super();

        this.model   = model;
        this.service = service;

        if (data) {
            if (!super.isDataValid(data)) {
                throw new Error('Collection data should be an array, ' + typeof data + ' given');
            }

            this.boot(data);
        }
    }

    public boot(data: Array<any>) {

        const model = this.getModel();

        for (const item of data) {

            const createdModel = this.service && typeof this.service.createModel === 'function' ? this.service.createModel(item) : model['make'](item, this.service);

            this.list.push(createdModel);
        }

    }

    /**
     * Returns the model from the service if exists
     *
     * @returns {Model}
     */
    private getModel() {
        return (this.service && this.service.model) ? this.service.model : this.model;
    }

    /**
     * Add a new model to the collection and server
     *
     * @param {Object} object
     * @returns {Observable<any>}
     */
    public post(object: any) {
        // Create a plain object if it's an instance of Model
        if (object.discriminator === 'ModelInterface') {
            object = object.plain();
        }

        return new Observable(observer => {

            this.service.post(object).subscribe((item) => {
                this.push(item);
                observer.next(item);
            }, error => {
                observer.error(error);
            }, () => {
                observer.complete();
            });

        });

    }

    /**
     * Add a new item to the collection
     *
     * @param item
     * @returns {Model}
     */
    public push(item) {
        const model = this.getModel()['make'](item, this.service);

        this.list.push(model);

        return model;
    }

    /**
     * Adds a new item to the beginning of the collection.
     *
     * @param item The item to add to the beginning of the array.
     * @returns {any} The model after executing the unshift.
     */
    public unshift(item) {
        const model = this.getModel()['make'](item, this.service);

        this.list.unshift(model);

        return model;
    }

    /**
     * Delete item from collection and server
     *
     * @param {number} id
     * @param {string} key
     * @returns {Observable<any>}
     */
    public delete(id: number, key: string = 'id') {

        return new Observable(observer => {

            this.service.delete(id).subscribe(() => {
                this.forget(key, id);
                observer.next(this);
            }, () => {
                observer.error();
            });

        });

    }

    /**
     * Make a new model from the loaded service
     *
     * @param data
     * @returns {any}
     */
    public makeModel(data = {}) {

        return this.service.make(data);

    }

    public clone(items?: Array<any>) {
        const clone = Object.assign(new ApiCollection, this);

        if (items) {
            clone.clear();
            clone.fill(items);
        }

        return clone;
    }

    public plain(): Array<any> {
        return this.list.map((item) => {
            if (item.plain && typeof item.plain === 'function') {
                return item.plain();
            } else {
                return item;
            }
        });
    }

    /**
     * Count all items within the collection
     *
     * @returns {number}
     */
    public count() {

        return super.count();

    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/api/collection.ts